import React, { useEffect, useMemo, useRef } from "react";
import { sliceColorForIndex } from "../../utils/bondWheelHelpers";

const POINTER_TOP = 20;

function polarToCartesian(cx, cy, radius, angleDeg) {
  const angle = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
}

function describeSlice(cx, cy, radius, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? "0" : "1";
  return [`M ${cx} ${cy}`, `L ${start.x} ${start.y}`, `A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`, "Z"].join(" ");
}

function sliceLabelName(participant, sliceCount) {
  if (participant?.isGhost) return participant.name || "Open";
  const name = participant?.user?.name || participant?.name || "Player";
  const maxLen = sliceCount <= 4 ? 12 : sliceCount <= 8 ? 8 : 6;
  return name.length > maxLen ? `${name.slice(0, maxLen - 1)}…` : name;
}

const SpinWheel = ({
  participants = [],
  size = 280,
  winningSliceIndex = null,
  winningSliceIndices = null,
  highlightUserId = null,
  spinning = false,
  settledRotationDeg = null,
  settleInstant = false,
}) => {
  const wheelRef = useRef(null);
  const radius = size / 2;
  const innerRadius = radius * 0.18;
  const labelRadius = radius * 0.62;

  useEffect(() => {
    const el = wheelRef.current;
    if (!el) return;
    if (spinning) {
      el.style.animation = "bw-wheel-spin 0.9s linear infinite";
      el.style.transform = "";
      return;
    }
    el.style.animation = "none";
    if (settledRotationDeg != null) {
      el.style.transition = settleInstant ? "none" : "transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)";
      el.style.transform = `rotate(${settledRotationDeg}deg)`;
    } else {
      el.style.transform = "rotate(0deg)";
    }
  }, [spinning, settledRotationDeg, settleInstant]);

  const winnerIndexSet = useMemo(() => {
    const indices =
      Array.isArray(winningSliceIndices) && winningSliceIndices.length > 0
        ? winningSliceIndices
        : Number.isFinite(winningSliceIndex)
        ? [winningSliceIndex]
        : [];
    return new Set(indices.filter((idx) => Number.isFinite(idx)));
  }, [winningSliceIndex, winningSliceIndices]);

  const safeParticipants = useMemo(() => {
    if (!participants?.length) {
      return [
        { id: "__ghost_a", name: "Open", isGhost: true },
        { id: "__ghost_b", name: "Open", isGhost: true },
      ];
    }
    if (participants.length === 1) {
      return [...participants, { id: "__ghost_b", name: "Awaiting", isGhost: true }];
    }
    return participants;
  }, [participants]);

  const sliceCount = safeParticipants.length;
  const anglePer = 360 / sliceCount;
  const labelFontSize = Math.max(8, Math.min(12, radius * 0.075 - sliceCount * 0.15));
  const hubSize = Math.max(innerRadius * 2.2, 58);
  const gradId = useMemo(() => `bw-${Math.random().toString(36).slice(2, 9)}`, []);

  return (
    <div className="bw-wheel-wrap" style={{ width: size, height: size + POINTER_TOP, paddingTop: POINTER_TOP }}>
      <div ref={wheelRef} className="bw-wheel-rotor">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <defs>
            <radialGradient id={`${gradId}-hub`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#1A2335" />
              <stop offset="100%" stopColor="#0A0F18" />
            </radialGradient>
            <radialGradient id={`${gradId}-glow`} cx="50%" cy="50%" r="50%">
              <stop offset="60%" stopColor="rgba(0,0,0,0)" />
              <stop offset="100%" stopColor="rgba(30,136,229,0.4)" />
            </radialGradient>
          </defs>
          <circle cx={radius} cy={radius} r={radius} fill={`url(#${gradId}-glow)`} />
          {safeParticipants.map((p, i) => {
            const startAngle = i * anglePer;
            const endAngle = (i + 1) * anglePer;
            const centerAngle = startAngle + anglePer / 2;
            const labelPos = polarToCartesian(radius, radius, labelRadius, centerAngle);
            const isHighlighted =
              highlightUserId &&
              String(p?.user?._id || p?.user || p?.id) === String(highlightUserId);
            const isWinner = winnerIndexSet.has(i);
            const path = describeSlice(radius, radius, radius - 4, startAngle, endAngle);
            return (
              <g key={`${p?.id || p?._id || i}-${i}`}>
                <path
                  d={path}
                  fill={p?.isGhost ? "rgba(255,255,255,0.04)" : sliceColorForIndex(i)}
                  fillOpacity={p?.isGhost ? 0.6 : 0.92}
                  stroke={isWinner ? "#64B5F6" : isHighlighted ? "#fff" : "rgba(0,0,0,0.45)"}
                  strokeWidth={isWinner ? 3 : isHighlighted ? 2.5 : 1}
                />
                <text
                  x={labelPos.x}
                  y={labelPos.y + 4}
                  fill={p?.isGhost ? "rgba(255,255,255,0.45)" : "#fff"}
                  fontSize={labelFontSize}
                  fontWeight="800"
                  textAnchor="middle"
                  transform={`rotate(${centerAngle} ${labelPos.x} ${labelPos.y})`}
                >
                  {sliceLabelName(p, sliceCount)}
                </text>
              </g>
            );
          })}
          <circle cx={radius} cy={radius} r={innerRadius} fill={`url(#${gradId}-hub)`} stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
          <circle cx={radius} cy={radius} r={innerRadius - 4} fill="#1A2335" />
        </svg>
      </div>
      <div className="bw-wheel-pointer" aria-hidden />
      <div
        className="bw-wheel-hub"
        style={{
          width: hubSize,
          height: hubSize,
          top: POINTER_TOP + radius - hubSize / 2,
          left: radius - hubSize / 2,
        }}
      >
        <span className="bw-wheel-hub-label">Tickets</span>
        <span className="bw-wheel-hub-count">{participants?.length || 0}</span>
      </div>
    </div>
  );
};

export default SpinWheel;
